/**
 * Platform settings that can be changed from the dashboard without a restart (D-32).
 * Settings needed before the database is readable, or that are dangerous to get wrong, stay in env.
 */
import { z } from "zod";
import { MESSAGE_PRIORITIES } from "../constants";
import { IsoDate } from "./common";

export const LOG_LEVELS = ["fatal", "error", "warn", "info", "debug", "trace", "silent"] as const;
export type LogLevel = (typeof LOG_LEVELS)[number];

/** Origin without path or trailing slash, e.g. `https://dashboard.aiyo.id`. */
export const Origin = z
  .string()
  .regex(/^https?:\/\/[^/\s?#]+$/, "Origin must look like https://example.com (no path, no trailing slash)");

export const WorkerConcurrency = z.object({
  high: z.int().min(1).max(200).default(10),
  normal: z.int().min(1).max(200).default(10),
  low: z.int().min(1).max(200).default(5),
});

/** A MapLibre style URL, or empty for the built-in default (OpenFreeMap, D-43). */
const MapStyleUrl = z.union([z.url(), z.literal("")]);

/** Built-in map styles when the settings leave them empty. */
export const DEFAULT_MAP_STYLE = {
  light: "https://tiles.openfreemap.org/styles/positron",
  dark: "https://tiles.openfreemap.org/styles/dark",
} as const;

export const SystemSettings = z
  .object({
    public_url: z
      .union([z.url(), z.literal("")])
      .default("")
      .meta({ description: "Public URL of the gateway, shown in the OpenAPI spec." }),
    trusted_origins: z.array(Origin).max(50).default([]).meta({
      description:
        "Extra origins allowed to call /admin with the session cookie. Merged with env TRUSTED_ORIGINS.",
    }),
    session_ttl_hours: z
      .int()
      .min(1)
      .max(720)
      .default(168)
      .meta({ description: "Lifetime of new dashboard sessions." }),
    provider_timeout_ms: z
      .int()
      .min(1000)
      .max(120_000)
      .default(30_000)
      .meta({ description: "Timeout of a single provider call." }),
    worker_concurrency: WorkerConcurrency.default({ high: 10, normal: 10, low: 5 }).meta({
      description: "Parallel jobs per lane in each worker. Applied without a restart.",
    }),
    paused_lanes: z.array(z.enum(MESSAGE_PRIORITIES)).max(MESSAGE_PRIORITIES.length).default([]).meta({
      description:
        "Priority lanes no worker processes (D-45). Their messages stay queued and are sent once the lane is resumed.",
    }),
    log_level: z.enum(LOG_LEVELS).default("info"),
    map_style_light: MapStyleUrl.default("").meta({
      description: "MapLibre style for the customer map in the light theme. Empty uses OpenFreeMap positron.",
    }),
    map_style_dark: MapStyleUrl.default("").meta({
      description: "MapLibre style for the customer map in the dark theme. Empty uses OpenFreeMap dark.",
    }),
  })
  .meta({ id: "SystemSettings" });
export type SystemSettings = z.infer<typeof SystemSettings>;

export const UpdateSystemSettingsRequest = z
  .strictObject({
    public_url: z.union([z.url(), z.literal("")]).optional(),
    trusted_origins: z.array(Origin).max(50).optional(),
    session_ttl_hours: z.int().min(1).max(720).optional(),
    provider_timeout_ms: z.int().min(1000).max(120_000).optional(),
    worker_concurrency: WorkerConcurrency.partial().optional(),
    paused_lanes: z.array(z.enum(MESSAGE_PRIORITIES)).max(MESSAGE_PRIORITIES.length).optional(),
    log_level: z.enum(LOG_LEVELS).optional(),
    map_style_light: MapStyleUrl.optional(),
    map_style_dark: MapStyleUrl.optional(),
  })
  .meta({ id: "UpdateSystemSettingsRequest" });
export type UpdateSystemSettingsRequest = z.infer<typeof UpdateSystemSettingsRequest>;

/** Env values shown read-only in the dashboard. Secrets are only reported as set or not set. */
export const EnvironmentInfo = z
  .object({
    node_env: z.string(),
    port: z.int(),
    database_pool_max: z.int(),
    database_url_set: z.boolean(),
    credential_master_key_set: z.boolean(),
    credential_key_version: z.int(),
    cookie_secure: z.boolean(),
    cookie_samesite: z.string(),
    trusted_origins: z.array(z.string()),
  })
  .meta({ id: "EnvironmentInfo" });
export type EnvironmentInfo = z.infer<typeof EnvironmentInfo>;

export const SystemSettingsView = z
  .object({
    settings: SystemSettings,
    updated_at: IsoDate.nullable(),
    updated_by: z.string().nullable(),
    environment: EnvironmentInfo,
  })
  .meta({ id: "SystemSettingsView" });
export type SystemSettingsView = z.infer<typeof SystemSettingsView>;

/** Settings every signed-in dashboard user may read, resolved to their effective values. */
export const DashboardConfig = z
  .object({
    map_style: z.object({ light: z.string(), dark: z.string() }),
  })
  .meta({ id: "DashboardConfig" });
export type DashboardConfig = z.infer<typeof DashboardConfig>;
